import React from 'react'
import PropTypes from 'prop-types'
import createMetronomeWorkerUrl from './metronome.worker'

import {
  ACTION_START,
  ACTION_STOP,
  ACTION_UPDATE,
  ACTION_TICK,
  TICKS_PER_BEAT_BINARY,
  TICKS_PER_BEAT_TERNARY,
  SECONDS_IN_MINUTE,
  SCHEDULE_AHEAD_TIME,
} from './constants'

class Metronome extends React.Component {
  static propTypes = {
    tempo: PropTypes.number,
    beatsPerMeasure: PropTypes.number,
    subdivision: PropTypes.number,
    autoplay: PropTypes.bool,
    beatVolume: PropTypes.number,
    subdivisionVolume: PropTypes.number,
    render: PropTypes.func,
    onTick: PropTypes.func,
    onSubtick: PropTypes.func,
    onStart: PropTypes.func,
    onStop: PropTypes.func,
    onAudioStateChange: PropTypes.func,
  }

  static defaultProps = {
    tempo: 120,
    beatsPerMeasure: 4,
    subdivision: 1,
    beatVolume: 1,
    subdivisionVolume: 0.5,
    autoplay: false,
    render: () => null,
    onTick: () => {},
    onSubtick: () => {},
    onStart: () => {},
    onStop: () => {},
    onAudioStateChange: () => {},
  }

  constructor(props) {
    super(props)

    if (this.props.subdivision < 1 || this.props.subdivision > 4) {
      throw new Error(
        `the \`subdivision\` prop must be between 1 and 4.`
      )
    }

    this.ticksPerBeat =
      this.props.beatsPerMeasure % 3 === 0 || this.props.subdivision % 3 === 0
        ? TICKS_PER_BEAT_TERNARY
        : TICKS_PER_BEAT_BINARY
    this.timerWorker = null
    this.audioContext = null
    this.workerUrl = null
    this.sampleBuffer = null
    this.sampleAbortController = null
    this.nextNoteTime = 0
    this.currentBeat = 0

    this.state = {
      beat: 0,
      subBeat: 0,
      playing: this.props.autoplay === true,
      tempo: this.props.tempo,
      beatsPerMeasure: this.props.beatsPerMeasure,
      subdivision: this.props.subdivision,
    }
  }

  componentDidMount() {
    this.prepareAudio()
  }

  prepareAudio = () => {
    this.releaseResources()
    this.props.onAudioStateChange('loading')
    // Constructors can be discarded by React without ever mounting.
    // Allocate browser resources only once the component actually mounts.
    try {
      this.workerUrl = createMetronomeWorkerUrl()
      this.timerWorker = new Worker(this.workerUrl)
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    } catch (error) {
      this.releaseResources()
      this.props.onAudioStateChange('error')
      console.error('Metronome audio could not start', error)
      return
    }

    this.timerWorker.onmessage = event => {
      if (event.data === ACTION_TICK) {
        this.runScheduler()
      }
    }

    const context = this.audioContext
    const controller = new AbortController()
    this.sampleAbortController = controller
    fetch('/samples/metronome.mp3', { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error('Could not load metronome sample')
        return response.arrayBuffer()
      })
      .then(data => controller.signal.aborted ? null : context.decodeAudioData(data))
      .then(buffer => {
        if (controller.signal.aborted) return
        this.sampleBuffer = buffer
        this.props.onAudioStateChange('ready')
        // Loading may finish after Play: start scheduling from now.
        this.nextNoteTime = context.currentTime
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          if (this.state.playing) this.stop()
          this.props.onAudioStateChange('error')
          console.error('Metronome sample failed to load', error)
        }
      })

    this.state.playing && this.start()
  }

  componentWillUnmount() {
    this.releaseResources()
  }

  releaseResources = () => {
    this.sampleAbortController?.abort()
    this.sampleAbortController = null
    this.sampleBuffer = null
    if (this.timerWorker) {
      this.timerWorker.onmessage = null
      this.timerWorker.terminate()
      this.timerWorker = null
    }
    if (this.audioContext) {
      if (this.audioContext.state !== 'closed') this.audioContext.close()
      this.audioContext = null
    }
    if (this.workerUrl) {
      URL.revokeObjectURL(this.workerUrl)
      this.workerUrl = null
    }
  }

  runScheduler = () => {
    if (!this.sampleBuffer) return
    const scheduleUntil = this.audioContext.currentTime + SCHEDULE_AHEAD_TIME
    let lateTickHandled = false
    while (
      this.nextNoteTime <
      scheduleUntil
    ) {
      const now = this.audioContext.currentTime
      if (this.nextNoteTime < now &&
          (lateTickHandled || this.nextNoteTime < now - SCHEDULE_AHEAD_TIME)) {
        // Skip expired positions in one operation, without sounding or ramping
        // missed beats. Retain phase on the grid at the current tempo.
        const secondsPerTick = this.state.beatsPerMeasure / this.ticksPerBeat *
          SECONDS_IN_MINUTE / this.state.tempo
        const skipped = Math.ceil((now - this.nextNoteTime) / secondsPerTick)
        this.nextNoteTime += skipped * secondsPerTick
        this.currentBeat = (this.currentBeat + skipped) % this.ticksPerBeat
        continue
      }
      // Allow one slightly late position for ordinary worker jitter/startup,
      // but never collapse a second expired position onto the same audio time.
      lateTickHandled = this.nextNoteTime < now
      this.tick(this.currentBeat, this.nextNoteTime)

      const secondsPerBeat = SECONDS_IN_MINUTE / this.state.tempo
      this.nextNoteTime +=
        this.state.beatsPerMeasure / this.ticksPerBeat * secondsPerBeat
      this.currentBeat++

      if (this.currentBeat === this.ticksPerBeat) {
        this.currentBeat = 0
      }
    }
  }

  tick = (beat, time) => {
    const isFirstBeat = beat === 0
    const isQuarterBeat =
      beat % (this.ticksPerBeat / this.state.beatsPerMeasure) === 0
    const isTripletBeat =
      this.ticksPerBeat === TICKS_PER_BEAT_TERNARY &&
      beat % (this.ticksPerBeat / this.state.beatsPerMeasure) !== 0
    const isEighthBeat =
      beat % (this.ticksPerBeat / (this.state.beatsPerMeasure * 2)) === 0

    const playTick =
      this.state.subdivision === 4 ||
      (this.state.subdivision === 3 && isTripletBeat) ||
      (this.state.subdivision === 2 && isEighthBeat) ||
      isQuarterBeat || isFirstBeat
    const volume = isFirstBeat || isQuarterBeat
      ? this.props.beatVolume : this.props.subdivisionVolume

    if (isFirstBeat || isQuarterBeat) {
      this.setState(
        state => ({
          beat:
            state.beat === this.state.beatsPerMeasure ? 1 : state.beat + 1 || 1,
        }),
        () => {
          this.props.onTick(this.state)
        }
      )
    }

    if (playTick) {
      if (this.sampleBuffer) {
        const startTime = Math.max(time, this.audioContext.currentTime)
        const source = this.audioContext.createBufferSource()
        const gainNode = this.audioContext.createGain()
        source.buffer = this.sampleBuffer
        source.connect(gainNode)
        gainNode.connect(this.audioContext.destination)
        gainNode.gain.setValueAtTime(volume, startTime)
        source.onended = () => {
          source.disconnect()
          gainNode.disconnect()
        }
        source.start(startTime)
      }

      this.setState(
        state => ({
          subBeat:
            state.subBeat === this.state.subdivision
              ? 1
              : state.subBeat + 1 || 1,
        }),
        () => {
          this.props.onSubtick(this.state)
        }
      )
    }
  }

  start = () => {
    this.currentBeat = 0
    this.nextNoteTime = this.audioContext.currentTime

    this.timerWorker.postMessage({
      action: ACTION_START,
      tempo: this.state.tempo,
      subdivision: this.state.subdivision,
    })

    this.setState(
      {
        beat: 0,
        playing: true,
      },
      () => {
        this.props.onStart(this.state)
      }
    )
  }

  stop = () => {
    this.timerWorker.postMessage({
      action: ACTION_STOP,
    })

    this.setState(
      {
        playing: false,
      },
      () => {
        this.props.onStop(this.state)
      }
    )
  }

  onPlay = async () => {
    if (this.state.playing) {
      this.stop()
      return
    }
    const context = this.audioContext
    if (!context) return
    try {
      if (context.state === 'suspended') await context.resume()
      // Navigation or Retry may replace the context while resume is pending.
      if (this.audioContext !== context) return
      this.start()
    } catch (error) {
      if (this.audioContext !== context) return
      this.props.onAudioStateChange('error')
      console.error('Metronome audio could not start', error)
    }
  }

  onTempoChange = tempo => {
    this.timerWorker?.postMessage({
      action: ACTION_UPDATE,
    })

    this.setState({
      tempo,
    })
  }

  render() {
    return this.props.render({
      ...this.state,
      onTempoChange: this.onTempoChange,
      onPlay: this.onPlay,
      onRetryAudio: this.prepareAudio,
    })
  }
}

export default Metronome
